import { FC, Fragment, useContext, useEffect } from 'react';
import { Box, Spinner } from '@chakra-ui/react';

import {
  useSummaries,
  useSummary,
  useSummaryComponents,
} from '~/core/shared/api/middle-office/summary';
import {
  Summary,
  SummaryContent,
  SummaryContentComponentElement,
} from '~/core/shared/types';
import { buildComponent } from '~/core/shared/utils/general';
import { SummaryTabBlockConfig } from '../../../config';
import { DefinitionContext } from '../../../contexts';
import { ApplicationContext } from '../Layout.component';
import { SummaryTabContext } from '../tabs';
import { ApplicationQuickInfo } from './ApplicationQuickInfo.component';
import { SummaryBlockElement } from './SummaryBlockElement';

type SummaryContentQueryParams = {
  summary: Summary | undefined;
  summaryComponents: SummaryContentComponentElement[] | undefined;
  contentId?: string;
  content?: SummaryContent;
  contentView?: string;
};

type SummaryContentQueryResult = {
  content?: SummaryContent;
  contentElements: Record<string, SummaryContentComponentElement[]>;
};

const querySummaryContentData = ({
  summary,
  summaryComponents,
  contentId,
  content,
  contentView,
}: SummaryContentQueryParams): SummaryContentQueryResult => {
  let contentElements:
    | Record<string, SummaryContentComponentElement[]>
    | undefined;
  let contentClone: SummaryContent | undefined = content;

  if (summary && summaryComponents) {
    if (!contentClone) {
      contentClone = summary?.template?.contents?.filter((c) => {
        if (contentId) {
          return c.id === contentId;
        }
        if (contentView) {
          return c.view === contentView;
        }
        return false;
      })[0];
    }

    const allowedComponentElements =
      contentClone && contentClone.components
        ? contentClone.components.map((c) => c.id)
        : summaryComponents.map((c) => c.id);

    contentElements = summaryComponents
      .filter((e) => allowedComponentElements.includes(e.id))
      .reduce(
        (acc, element) => {
          if (!acc[element.document]) {
            acc[element.document] = [];
          }
          acc[element.document].push(element);
          return acc;
        },
        {} as Record<string, SummaryContentComponentElement[]>,
      );
  }

  return {
    content: contentClone,
    contentElements: contentElements || {},
  };
};

export type SummaryContentComponent = {
  summaryTemplate: string;
};

const customComponents: Record<string, SummaryTabBlockConfig> = {
  'application-quick-info': {
    view: {
      component: ApplicationQuickInfo,
    },
  },
};

export const SummaryContentComponent: FC<SummaryContentComponent> = ({
  summaryTemplate,
}) => {
  const { applicationUuid: uuid, lastApplicationUpdate } =
    useContext(ApplicationContext);
  const { definition } = useContext(DefinitionContext);
  const { getBlockConfig } = useContext(SummaryTabContext);

  const { summaries, refetch: refetchSummaries } = useSummaries(
    definition,
    summaryTemplate,
  );
  const firstSummary = summaries?.[0];
  const { summary } = useSummary(definition, firstSummary?.id ?? '', {
    enabled: !!firstSummary?.id,
  });
  const { summaryComponents } = useSummaryComponents(
    definition,
    summary?.id,
    uuid,
  );

  useEffect(() => {
    refetchSummaries();
  }, [lastApplicationUpdate]);

  const hasLoadedSummary = summary && summary.template && summaryComponents;

  const blocks = summary?.template?.contents;

  if (!hasLoadedSummary) {
    return <Spinner />;
  }

  return (
    <>
      {hasLoadedSummary && (
        <Box>
          {blocks?.map((summaryContent: SummaryContent, index) => {
            const blockConfig = {
              ...(customComponents[summaryContent.identifier] || {}),
              ...getBlockConfig(summaryContent.identifier),
            };

            const { contentElements } = querySummaryContentData({
              summaryComponents,
              content: summaryContent,
              summary,
            });

            if (blockConfig.view) {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={index}>
                  {buildComponent(blockConfig.view, {
                    contentElements: blockConfig.multipleDocumentsContext
                      ? contentElements
                      : contentElements[uuid],
                    summary,
                  })}
                </Fragment>
              );
            }

            return (
              <SummaryBlockElement
                key={`${summaryContent.blockId}-${summaryContent.id}`}
                content={summaryContent}
                contentElements={contentElements}
                documentType={definition}
              />
            );
          })}
        </Box>
      )}
    </>
  );
};
